/**
 * 展会日期（`Event.dates`）在**前端**的规范化实现 —— 全仓唯一一份
 * （与 `money.ts` / `orderNo.ts` 同款纪律：同一件事不许在应用里再写一遍）。
 *
 * 数据流：日期行列表（`DateListField` 的 `value: string[]`，允许 `''` 表示"这一行还没选"）
 *   → 本函数 → `POST /events` / `POST /organizer/events` 的 `dates`
 *   （契约 §11.1 / §23.2：**非空**数组，元素是 `YYYY-MM-DD`）。
 *
 * ## 为什么"去空 / 去重 / 升序"三件事都放在这里
 *
 * 它们都是**行列表这种控件的固有噪声**，不是业务判断：
 *   - **去空**：行是用户手动加的，加完没选日期是常态；空串发上去会被 `@IsDateString()`
 *     打回 `400 VALIDATION_FAILED`，而用户看到的是"我明明填了日期"。
 *   - **去重**：同一场展会填两遍同一天没有意义，落库后却会在列表里渲染成
 *     「2026-08-15、2026-08-15」。
 *   - **升序**：`YYYY-MM-DD` 的字典序恰好等于时间序（`settlement-plan.ts:218` 的
 *     `DATE_ONLY` 注释记的是同一条），因此直接比字符串即可，不必解析成 `Date`。
 *     服务端取结算期间走的是 `beijingDatesRange` 的 **min/max**，与数组顺序无关
 *     ⇒ 排序只影响展示口径，不影响结算。
 *
 * ## 不在这里校验形状（`^\d{4}-\d{2}-\d{2}$`）
 *
 * 形状的权威在服务端（`@IsDateString()`；结算另有一条更严的形状检查，非法即 422）。
 * 前端这一层唯一的生产者是 `<input type="date">`，它只会给出合法日历日或空串；
 * 在这里再判一次并**静默丢弃**非法值，会把"数据有问题"变成"少了一天"。
 *
 * @param rows 行值（含未选择的空行）
 * @returns 可直接放进请求体的日期数组（已去空、去重、升序）
 */
export function normalizeDates(rows: readonly string[]): string[] {
  const seen = new Set<string>();
  const days: string[] = [];
  for (const row of rows) {
    const day = row.trim();
    if (day === '' || seen.has(day)) continue;
    seen.add(day);
    days.push(day);
  }
  return days.sort();
}
